/**
 * O login da tela, falando com a P3.1B (`/api/auth/*`).
 *
 * 🚨 CADA FALHA TEM O SEU NOME. O design (tela 04) separa "credenciais inválidas", "sem conexão",
 * "serviço indisponível" e "erro inesperado" — e isso não é capricho: dizer "confira seus dados"
 * para quem está sem internet faz a pessoa redigitar a senha certa cinco vezes e travar a conta
 * no limite de tentativas. Rede fora é rede fora; servidor fora é servidor fora.
 *
 * 🚨 O CONTRÁRIO TAMBÉM VALE: recusa é UMA mensagem só. O servidor responde igual para login
 * inexistente, senha errada e conta inativa (anti-enumeração), e a tela não tenta adivinhar qual
 * foi — "nenhum estado identifica qual dado está errado" (design, estados de autenticação).
 *
 * A sessão mora num cookie HttpOnly que o JavaScript não lê. O que fica em memória é só o token
 * CSRF, que toda escrita manda no cabeçalho `x-csrf-token`.
 */
import { buscar } from './buscar';

export const CABECALHO_CSRF = 'x-csrf-token';

export type Sessao = {
  readonly criadaEm: string;
  readonly expiraEm: string;
  readonly csrfToken: string;
};

export type FalhaDeLogin =
  | { readonly tipo: 'credenciais' }
  | { readonly tipo: 'limite' }
  | { readonly tipo: 'offline' }
  | { readonly tipo: 'indisponivel' }
  | { readonly tipo: 'inesperado'; readonly codigo: string | null };

export type ResultadoDoLogin =
  | { readonly ok: true; readonly sessao: Sessao }
  | { readonly ok: false; readonly falha: FalhaDeLogin };

type RespostaDeSessao =
  | {
      readonly authenticated: true;
      readonly session: { readonly criadaEm: string; readonly expiraEm: string };
      readonly csrfToken: string;
    }
  | { readonly authenticated: false };

function lerSessao(corpo: RespostaDeSessao): Sessao | null {
  if (!corpo.authenticated) return null;
  return {
    criadaEm: corpo.session.criadaEm,
    expiraEm: corpo.session.expiraEm,
    csrfToken: corpo.csrfToken,
  };
}

export async function entrar(login: string, senha: string): Promise<ResultadoDoLogin> {
  let resposta: Response;
  try {
    resposta = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ login, senha }),
    });
  } catch {
    // fetch só rejeita quando a requisição nem chegou a sair (rede, DNS, servidor desligado).
    return { ok: false, falha: { tipo: 'offline' } };
  }

  if (resposta.ok) {
    const sessao = lerSessao((await resposta.json()) as RespostaDeSessao);
    if (sessao) return { ok: true, sessao };
    return {
      ok: false,
      falha: { tipo: 'inesperado', codigo: resposta.headers.get('x-request-id') },
    };
  }
  if (resposta.status === 401 || resposta.status === 400)
    return { ok: false, falha: { tipo: 'credenciais' } };
  if (resposta.status === 429) return { ok: false, falha: { tipo: 'limite' } };
  if (resposta.status === 502 || resposta.status === 503 || resposta.status === 504) {
    return { ok: false, falha: { tipo: 'indisponivel' } };
  }
  return { ok: false, falha: { tipo: 'inesperado', codigo: resposta.headers.get('x-request-id') } };
}

/** A sessão atual, se houver. Falha de rede NÃO vira "deslogado": devolve `undefined`. */
export async function sessaoAtual(): Promise<Sessao | null | undefined> {
  try {
    const resposta = await buscar('/api/auth/session', { credentials: 'same-origin' });
    if (!resposta.ok) return undefined;
    return lerSessao((await resposta.json()) as RespostaDeSessao);
  } catch {
    return undefined;
  }
}

/**
 * "Sair dos outros aparelhos" (o "sair de todos" do 1.0): esta sessão fica; as outras caem.
 * `null` = não deu para confirmar (rede) — a tela diz isso, nunca finge que saiu.
 */
export async function sairDosOutros(sessao: Sessao): Promise<number | null> {
  try {
    const r = await fetch('/api/auth/sair-dos-outros', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { [CABECALHO_CSRF]: sessao.csrfToken },
    });
    if (!r.ok) return null;
    return ((await r.json()) as { encerradas: number }).encerradas;
  } catch {
    return null;
  }
}

export async function sair(sessao: Sessao): Promise<void> {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { [CABECALHO_CSRF]: sessao.csrfToken },
    });
  } catch {
    // Mesmo sem rede a tela sai: o cookie expira sozinho e nada fica visível.
  }
}
